/**
 * Filter slice for the service-calls store (iter 370bq refactor — extracted
 * from `serviceCallsStore.js` to keep that file focused on data + handlers).
 *
 * Owns: searchTerm, statusFilter, activeTab, compactMode, tagFilter — all
 * persisted to localStorage so the dispatcher's view sticks across reloads.
 *
 * Imports `axios` rather than the parent's instance so this slice has no
 * implicit coupling. The 3+ char search-broadens-via-server fetch lives
 * here too since it's a side effect of `setSearchTerm`.
 */
import axios from "axios";
import BACKEND_URL from "../../lib/apiConfig";
import { SERVICE_STATUSES } from "../../lib/serviceCallStatuses";

const API = `${BACKEND_URL}/api`;

const loadPersisted = (key, fallback) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved === null) return fallback;
    return JSON.parse(saved);
  } catch {
    return fallback;
  }
};

const AIRTABLE_STATUSES_ALL = SERVICE_STATUSES;

export const createFiltersSlice = (set, get) => ({
  // ===== Filter state (persisted) =====
  searchTerm: "",
  statusFilter: [...AIRTABLE_STATUSES_ALL],
  activeTab: localStorage.getItem("serviceCalls_activeTab") || "all",
  compactMode: localStorage.getItem("serviceCalls_compactMode") === "true",
  tagFilter: loadPersisted("serviceCalls_tagFilter", []),

  setSearchTerm: (v) => {
    localStorage.setItem("serviceCalls_searchTerm", v);
    set({ searchTerm: v });
    // When searching 3+ chars, also fetch from server to include inactive
    // matches. iter 370bd — capped at 100 results so a broad search can't
    // dump the whole include_inactive payload through the pipe.
    if (v && v.trim().length >= 3) {
      const term = v.trim();
      axios.get(`${API}/service-calls?search=${encodeURIComponent(term)}&include_inactive=true&page=1&page_size=100`)
        .then((res) => {
          if (get().searchTerm.trim() !== term) return; // stale
          const serverCalls = res.data?.items || [];
          if (serverCalls.length === 0) return;
          const existing = new Set(get().calls.map((c) => c.id));
          const newCalls = serverCalls.filter((c) => !existing.has(c.id));
          if (newCalls.length > 0) {
            set({ calls: [...get().calls, ...newCalls] });
          }
        })
        .catch(() => {});
    }
  },

  setStatusFilter: (v) => {
    const val = typeof v === "function" ? v(get().statusFilter) : v;
    // (No Status) is always pinned in front so a freshly-Airtable-imported
    // call without a status doesn't disappear from the list.
    const withNoStatus = val.includes("(No Status)") ? val : ["(No Status)", ...val];
    localStorage.setItem("serviceCalls_statusFilter", JSON.stringify(withNoStatus));
    set({ statusFilter: withNoStatus });
  },

  setActiveTab: (v) => {
    localStorage.setItem("serviceCalls_activeTab", v);
    set({ activeTab: v });
  },

  setCompactMode: (v) => {
    localStorage.setItem("serviceCalls_compactMode", v.toString());
    set({ compactMode: v });
  },

  setTagFilter: (v) => {
    const val = typeof v === "function" ? v(get().tagFilter) : v;
    localStorage.setItem("serviceCalls_tagFilter", JSON.stringify(val));
    set({ tagFilter: val });
  },
});
